import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';

import { FeatureFlagKeys, selectFeatureFlag } from '@ninety/platform/state';
import { selectIsThriveOrFreemiumPlan } from '@ninety/platform/state/app-global/billing/billing-state.selectors';
import { TerraErrorModule, TerraFormFieldModule, TerraIconModule, TerraTextInputModule } from '@ninety/terra';
import { ButtonComponent, ButtonRowComponent, NinetyChipComponent } from '@ninety/ui/core';
import {
  CalendarEvent,
  CalendarEventsService,
  ChooseCalendarProviderDialogComponent,
  GoogleCalendarService,
  isOutlookCalendarProvider,
  NativeIntegration,
  NativeIntegrationCoreService,
  OutlookCalendarService,
} from '@ninety/web/pages/shared/integrations';

import { SelectCalendarEventDialogComponent } from '../../meetings-page/start-meeting-dialog/select-calendar-event-dialog/select-calendar-event-dialog.component';
import { MeetingBotCalendarSectionComponent } from '../meeting-bot-calendar-section/meeting-bot-calendar-section.component';
import { validateMeetingLink } from '../util/meeting-link.validator';

@Component({
  selector: 'ninety-add-meeting-bot-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TerraFormFieldModule,
    TerraTextInputModule,
    TerraErrorModule,
    TerraIconModule,
    ButtonComponent,
    NinetyChipComponent,
    ButtonRowComponent,
    MatDialogModule,
    SelectCalendarEventDialogComponent,
    MeetingBotCalendarSectionComponent,
    ChooseCalendarProviderDialogComponent,
  ],
  templateUrl: './add-meeting-bot-dialog.component.html',
  styles: [
    `
      @use 'terra';

      :host {
        display: flex;
        flex-direction: column;
        width: 480px;
        position: relative;
      }
      .close-dialog-btn {
        position: absolute;
        top: 8px;
        right: 8px;
      }
      .meeting-link-field {
        width: 100%;
      }
      .meeting-link-divider {
        display: block;
        margin-bottom: 8px;
      }
      .meeting-link-valid-icon {
        color: terra.color('green');
      }
      mat-dialog-content {
        padding: 20px !important;

        &.dialog-container--unpadded {
          padding: 0 !important;
        }
      }
    `,
  ],
})
export class AddMeetingBotDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<AddMeetingBotDialogComponent, string | undefined>);
  private readonly nativeIntegrationService = inject(NativeIntegrationCoreService);
  private readonly store = inject(Store);
  private readonly googleCalendarService = inject(GoogleCalendarService);
  private readonly outlookCalendarService = inject(OutlookCalendarService);

  // This dialog's whole purpose is adding the bot, so the toggle inside
  // MeetingBotCalendarSectionComponent is locked on rather than user-controlled.
  readonly addMeetingAgentControl = new FormControl(true);
  readonly meetingLinkControl = new FormControl('', validateMeetingLink);

  // Mirrors MeetingBotCalendarSectionComponent's own gates, just to show/hide the "or" divider
  // above the link field, which lives outside that component rather than as projected content.
  private readonly calendarConnectEnabled = toSignal(
    this.store.select(selectFeatureFlag(FeatureFlagKeys.webCalendarConnect)),
    { initialValue: false }
  );
  private readonly isEntitledTier = toSignal(this.store.select(selectIsThriveOrFreemiumPlan), { initialValue: false });
  readonly showCalendarOption = computed(() => this.calendarConnectEnabled() && this.isEntitledTier());

  // Only needed here to resolve the connectionId for the swapped event picker below - the rest of
  // the calendar-connect UI/state lives in MeetingBotCalendarSectionComponent.
  readonly activeCalendarConnectionId = computed(() => this.nativeIntegrationService.activeCalendarConnectionId());

  readonly calendarEventsService = computed<CalendarEventsService>(() => {
    const integration = this.nativeIntegrationService.activeCalendarIntegration();
    return integration && isOutlookCalendarProvider(integration)
      ? this.outlookCalendarService
      : this.googleCalendarService;
  });

  readonly selectedCalendarEvent = signal<CalendarEvent | undefined>(undefined);

  // Swaps this dialog's own content for the event picker in place, rather than opening it as a
  // nested modal on top - same pattern as StartMeetingDialogComponent.
  readonly showEventPicker = signal(false);

  // Same swap-in-place pattern for the choose-provider step, so the meeting dialog stays in the
  // background instead of a second dialog stacking on top of it.
  readonly showProviderChooser = signal(false);
  readonly providerChooserIntegrations = signal<NativeIntegration[]>([]);

  constructor() {
    this.nativeIntegrationService.fetchNativeIntegrations();
  }

  openEventPicker(): void {
    if (!this.activeCalendarConnectionId()) {
      return;
    }
    this.showEventPicker.set(true);
  }

  onEventPickerClosed(event: CalendarEvent | undefined): void {
    this.showEventPicker.set(false);
    if (!event) {
      return;
    }
    this.selectedCalendarEvent.set(event);
    this.meetingLinkControl.setValue(event.meetingUrl ?? '');
  }

  onOpenProviderChooser(integrations: NativeIntegration[]): void {
    this.providerChooserIntegrations.set(integrations);
    this.showProviderChooser.set(true);
  }

  onProviderChooserClosed(): void {
    this.showProviderChooser.set(false);
  }

  // validateMeetingLink treats an empty value as valid, so presence is checked separately here.
  canAdd(): boolean {
    return !!this.selectedCalendarEvent() || (this.meetingLinkControl.valid && !!this.meetingLinkControl.value?.trim());
  }

  add() {
    const value = this.meetingLinkControl.value?.trim();
    if (!value) {
      this.meetingLinkControl.setErrors({ required: true });
      this.meetingLinkControl.markAsTouched();
      return;
    }

    if (this.meetingLinkControl.invalid) {
      this.meetingLinkControl.markAsTouched();
      return;
    }

    this.dialogRef.close(value);
  }

  cancel() {
    this.dialogRef.close();
  }
}
